import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { getStorageQuota } from '../../services/documentsApi'

/**
 * Smart storage formatter that picks the most appropriate unit for a SINGLE value.
 * Matches backend logic in server/documents/utils.py::format_storage_size
 *
 * Use this for standalone values like file sizes.
 * For comparing two values (current/max), use formatStoragePair() instead.
 */
const formatStorageSize = (bytes: number): string => {
  if (bytes === 0) return '0B'

  const KB = 1024
  const MB = 1024 * 1024
  const GB = 1024 * 1024 * 1024

  if (bytes < KB) {
    return `${bytes}B`
  } else if (bytes < 0.1 * MB) {
    // < 100KB, show in KB
    const kb = bytes / KB
    return kb < 10 ? `${kb.toFixed(1)}KB` : `${kb.toFixed(0)}KB`
  } else if (bytes < GB) {
    // < 1GB, show in MB
    const mb = bytes / MB
    if (mb < 10) {
      return `${mb.toFixed(1)}MB`
    } else if (mb < 100) {
      return `${mb.toFixed(0)}MB`
    } else {
      return `${mb.toFixed(0)}MB`
    }
  } else {
    // >= 1GB
    const gb = bytes / GB
    // Always show 1 decimal for GB to avoid confusion
    return `${gb.toFixed(1)}GB`
  }
}

/**
 * Format a value with adaptive decimal precision based on magnitude.
 * Matches backend logic in server/documents/utils.py::_format_value_with_unit
 *
 * Rules:
 * - < 0.01: Show up to 3 decimals (e.g., 0.007GB, not 0.0GB)
 * - < 0.1: Show 2 decimals (e.g., 0.05GB)
 * - >= 0.1: Show 1 decimal (e.g., 0.5GB, 1.0GB)
 */
const formatValueWithUnit = (value: number, unit: string): string => {
  if (value < 0.01) {
    // Very small: up to 3 decimals, remove trailing zeros
    let formatted = value.toFixed(3).replace(/\.?0+$/, '')
    // Ensure at least one decimal for consistency
    if (!formatted.includes('.')) {
      formatted = value.toFixed(1)
    }
    return `${formatted}${unit}`
  } else if (value < 0.1) {
    // Small: 2 decimals
    return `${value.toFixed(2)}${unit}`
  } else {
    // Normal: 1 decimal (always show it)
    return `${value.toFixed(1)}${unit}`
  }
}

/**
 * Format a pair of storage values using the SAME unit for easy comparison.
 * Matches backend logic in server/documents/utils.py::format_storage_pair
 *
 * Use this for current/max comparisons in storage displays.
 * For standalone values like file sizes, use formatStorageSize() instead.
 *
 * Rules:
 * - Both values use the same unit (determined by maxBytes)
 * - Adaptive decimal precision: 1-3 decimals based on value magnitude
 * - Prevents "0.0GB" for small values like 7MB (shows "0.007GB")
 * - Makes comparisons intuitive (0.5GB / 1.0GB, not 500MB / 1.0GB)
 */
const formatStoragePair = (
  currentBytes: number,
  maxBytes: number
): [string, string] => {
  if (maxBytes === 0) return ['0B', '0B']

  const KB = 1024
  const MB = 1024 * 1024
  const GB = 1024 * 1024 * 1024

  // Determine unit based on maxBytes (larger value)
  if (maxBytes < MB) {
    // Use KB
    const currentKb = currentBytes / KB
    const maxKb = maxBytes / KB
    return [
      formatValueWithUnit(currentKb, 'KB'),
      formatValueWithUnit(maxKb, 'KB'),
    ]
  } else if (maxBytes < GB) {
    // Use MB
    const currentMb = currentBytes / MB
    const maxMb = maxBytes / MB
    return [
      formatValueWithUnit(currentMb, 'MB'),
      formatValueWithUnit(maxMb, 'MB'),
    ]
  } else {
    // Use GB
    const currentGb = currentBytes / GB
    const maxGb = maxBytes / GB
    return [
      formatValueWithUnit(currentGb, 'GB'),
      formatValueWithUnit(maxGb, 'GB'),
    ]
  }
}

interface StorageQuotaDisplayProps {
  inline?: boolean
}

export const StorageQuotaDisplay: React.FC<StorageQuotaDisplayProps> = ({
  inline = false,
}) => {
  const { data: quota, isLoading } = useQuery({
    queryKey: ['documents', 'quota'],
    queryFn: getStorageQuota,
    staleTime: 1000 * 60, // 1 minute
  })

  if (isLoading || !quota) {
    return null
  }

  const percentageUsed = Math.min(quota.usage_percentage, 100)
  const barColor =
    percentageUsed >= 95
      ? 'bg-danger'
      : percentageUsed >= 80
        ? 'bg-warning'
        : 'bg-success'

  // Format storage values using paired formatting for comparison
  const [currentStr, maxStr] = formatStoragePair(
    quota.current_usage,
    quota.max_size
  )
  const remainingStr = formatStorageSize(quota.remaining_space)

  // Inline version for header
  if (inline) {
    return (
      <div
        style={{
          minWidth: '250px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
          Storage: {currentStr} / {maxStr}
        </div>
        <div className="progress" style={{ height: '8px', marginBottom: 0 }}>
          <div
            className={`progress-bar ${barColor}`}
            role="progressbar"
            style={{ width: `${percentageUsed}%` }}
            aria-valuenow={percentageUsed}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
    )
  }

  // Block version for table
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Storage Usage</h6>
        <small className="text-muted">
          {currentStr} of {maxStr} used
        </small>
      </div>
      <div className="progress" style={{ height: '20px' }}>
        <div
          className={`progress-bar ${barColor}`}
          role="progressbar"
          style={{ width: `${percentageUsed}%` }}
          aria-valuenow={percentageUsed}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {percentageUsed.toFixed(1)}%
        </div>
      </div>
      <small className="text-muted">{remainingStr} remaining</small>
    </div>
  )
}

StorageQuotaDisplay.propTypes = {
  inline: PropTypes.bool,
}
